import { forwardRef } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Lock, Plus, X } from 'lucide-react';
import { stepNumber } from '@/steps';

const pad = (i) => String(i + 1).padStart(2, '0');

// A tracking category: its name plus a numbered list of options (as in the sheet's Option A / Option B blocks).
function TrackingField({ field, value, onChange }) {
  const current = value || { name: '', options: ['', ''] };
  const setOption = (i, text) =>
    onChange({ ...current, options: current.options.map((o, j) => (j === i ? text : o)) });
  const removeOption = (i) =>
    onChange({ ...current, options: current.options.filter((_, j) => j !== i) });

  return (
    <div className="xs-tracking xs-wide">
      <label className="xs-label">{field.label}</label>
      <input
        className="xs-input"
        value={current.name}
        placeholder={field.placeholder}
        onChange={(e) => onChange({ ...current, name: e.target.value })}
      />
      <div className="xs-tracking-options">
        {current.options.map((option, i) => (
          <div className="xs-tracking-option" key={i}>
            <span className="xs-tracking-num">{pad(i)}</span>
            <input
              className="xs-input"
              value={option}
              placeholder={`Option ${pad(i)}`}
              onChange={(e) => setOption(i, e.target.value)}
            />
            <button type="button" className="xs-icon-btn" onClick={() => removeOption(i)} aria-label={`Remove option ${pad(i)}`}>
              <X size={16} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="xs-add-btn"
          onClick={() => onChange({ ...current, options: [...current.options, ''] })}
        >
          <Plus size={15} /> Add option
        </button>
      </div>
    </div>
  );
}

function Field({ field, value, onChange }) {
  if (field.type === 'tracking') return <TrackingField field={field} value={value} onChange={onChange} />;

  const id = `f-${field.name}`;
  let control;
  if (field.type === 'select') {
    control = (
      <select id={id} className="xs-input" value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">{field.options.length ? 'Select…' : field.emptyText}</option>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else if (field.type === 'yesno') {
    control = (
      <div className="xs-yesno" role="radiogroup" aria-labelledby={`${id}-label`}>
        {['Yes', 'No'].map((o) => (
          <label key={o} className={`xs-yesno-option${value === o ? ' is-selected' : ''}`}>
            <input type="radio" name={id} value={o} checked={value === o} onChange={() => onChange(o)} />
            {o}
          </label>
        ))}
      </div>
    );
  } else {
    control = (
      <input
        id={id}
        className="xs-input"
        type={field.type}
        value={value || ''}
        placeholder={field.placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <div className={`xs-field${field.wide ? ' xs-wide' : ''}`}>
      <label className="xs-label" id={`${id}-label`} htmlFor={field.type === 'yesno' ? undefined : id}>
        {field.label}
      </label>
      {control}
    </div>
  );
}

// One step in its own frame: number + name + status, then its settings, then the Process button below them.
const StepCard = forwardRef(function StepCard(
  { step, index, values, status, completedBy, locked, lockedReason, processing, notice, onFieldChange, onProcess, onMarkCompleted, onUndo },
  ref,
) {
  const completed = status === 'completed';
  const state = completed ? 'completed' : locked ? 'locked' : 'active';

  return (
    <section ref={ref} className={`xs-step is-${state}`} aria-labelledby={`step-${step.id}-title`}>
      <header className="xs-step-header">
        <div className="xs-step-num" aria-hidden="true">
          {completed ? <CheckCircle2 size={26} /> : locked ? <Lock size={20} /> : stepNumber(index)}
        </div>
        <div className="xs-step-heading">
          <div className="xs-step-kicker">STEP {stepNumber(index)}</div>
          <h2 className="xs-step-title" id={`step-${step.id}-title`}>{step.title}</h2>
        </div>
        <div className={`xs-status ${completed ? 'is-completed' : 'is-pending'}`} aria-live="polite">
          {completed ? 'Completed' : 'Pending…'}
        </div>
      </header>

      {/* A disabled fieldset disables every control inside it: locked steps and completed steps are read-only. */}
      <fieldset className="xs-step-body" disabled={locked || completed || processing}>
        {step.warning && (
          <div className="xs-warning"><AlertTriangle size={18} /> {step.warning}</div>
        )}
        {step.fields.length ? (
          <div className="xs-grid">
            {step.fields.map((field) => [
              field.group && <h3 className="xs-group" key={`g-${field.name}`}>{field.group}</h3>,
              <Field
                key={field.name}
                field={field}
                value={values[field.name]}
                onChange={(v) => onFieldChange(field.name, v)}
              />,
            ])}
          </div>
        ) : (
          <p className="xs-no-settings">No settings to fill in for this step.</p>
        )}
      </fieldset>

      {notice && <div className="xs-notice" role="status">{notice}</div>}

      <footer className="xs-step-footer">
        <div className="xs-override">
          {locked ? (
            <span className="xs-locked-reason"><Lock size={14} /> {lockedReason}</span>
          ) : completed ? (
            <span className="xs-completed-by">
              {completedBy === 'manual' ? 'Marked as completed manually' : 'Processed in Xero'}
              {completedBy === 'manual' && (
                <button type="button" className="xs-link-btn" onClick={onUndo}>Undo</button>
              )}
            </span>
          ) : (
            <button type="button" className="xs-link-btn" onClick={onMarkCompleted} disabled={processing}>
              Not needed? Mark as completed
            </button>
          )}
        </div>
        <button
          type="button"
          className="xs-process-btn"
          onClick={onProcess}
          disabled={locked || completed || processing}
        >
          {processing ? <><Loader2 size={18} className="xs-spin" /> Processing…</> : completed ? <><CheckCircle2 size={18} /> Done</> : 'Process'}
        </button>
      </footer>
    </section>
  );
});

export default StepCard;
